/* PDFCommunity - compare two PDFs (text differences), v147.
   The text of both PDFs is read with pdf.js (pcGetDocument: text in hidden
   layers is already left out), cut into words and compared over the whole
   document with Myers' O(ND) diff (linear-space "middle snake" version, as in
   diff-match-patch by Neil Fraser, written here for integer word ids).
   The diff runs in a small worker of its own: this same file, started with
   new Worker() (it has no `document` there and only answers diff requests),
   so a long comparison doesn't freeze the page and Cancel ends it at once.
   Without a worker the page runs the same function. A time limit keeps very
   different documents from running for minutes: what is left then is shown
   as whole passages removed/added (and the result says so).
   The download is a self-contained HTML report (no scripts, no external
   files): it shows any script (CJK too) with the reader's own fonts, where a
   PDF report would need an embedded font per script.
   The files are read by this page only and are not uploaded. */
(function(){
  'use strict';

  /* ---------- the diff (page and worker) ---------- */
  var DIFF_TIME_MS = 20000;      // after this, the rest is shown as whole passages (approx)
  var PROGRESS_EVERY_MS = 200;
  var TIMEOUT = 'timeout';
  function mark(flags, from, to){ for(var k = from; k < to; k++) flags[k] = 1; }
  /* The middle snake of a[a0..a0+N) vs b[b0..b0+M): [x, y] to split at; null when
     they have no word in common (all removed + all added is then exact); TIMEOUT. */
  function bisect(a, a0, N, b, b0, M, deadline){
    var maxD = Math.ceil((N + M) / 2), off = maxD, vlen = 2 * maxD + 2;
    var v1 = new Int32Array(vlen).fill(-1), v2 = new Int32Array(vlen).fill(-1);
    v1[off + 1] = 0; v2[off + 1] = 0;
    var delta = N - M, front = delta % 2 !== 0;
    var k1start = 0, k1end = 0, k2start = 0, k2end = 0;
    for(var d = 0; d < maxD; d++){
      if(Date.now() > deadline) return TIMEOUT;
      for(var k1 = -d + k1start; k1 <= d - k1end; k1 += 2){
        var i1 = off + k1;
        var x1 = (k1 === -d || (k1 !== d && v1[i1 - 1] < v1[i1 + 1])) ? v1[i1 + 1] : v1[i1 - 1] + 1;
        var y1 = x1 - k1;
        while(x1 < N && y1 < M && a[a0 + x1] === b[b0 + y1]){ x1++; y1++; }
        v1[i1] = x1;
        if(x1 > N) k1end += 2;
        else if(y1 > M) k1start += 2;
        else if(front){
          var j2 = off + delta - k1;
          if(j2 >= 0 && j2 < vlen && v2[j2] !== -1 && x1 >= N - v2[j2]) return [x1, y1];
        }
      }
      for(var k2 = -d + k2start; k2 <= d - k2end; k2 += 2){
        var i2 = off + k2;
        var x2 = (k2 === -d || (k2 !== d && v2[i2 - 1] < v2[i2 + 1])) ? v2[i2 + 1] : v2[i2 - 1] + 1;
        var y2 = x2 - k2;
        while(x2 < N && y2 < M && a[a0 + N - x2 - 1] === b[b0 + M - y2 - 1]){ x2++; y2++; }
        v2[i2] = x2;
        if(x2 > N) k2end += 2;
        else if(y2 > M) k2start += 2;
        else if(!front){
          var j1 = off + delta - k2;
          if(j1 >= 0 && j1 < vlen && v1[j1] !== -1){
            var xa = v1[j1];
            if(xa >= N - x2) return [xa, off + xa - j1];
          }
        }
      }
    }
    return null;
  }
  /* (Patience-style anchors) For a long stretch, Myers is O((N+M)·D): a course
     with thousands of scattered edits would take minutes. Three-word sequences
     that occur exactly once in each side are matched first (the longest run of
     them in the same order), then only the short gaps between them go to Myers.
     Not always the minimal diff, but the same changes a reader would point at. */
  var ANCHOR_MIN = 2000; // words on both sides together
  var MYERS_MAX = 12000;  // a longer stretch without any anchor is unrelated text: shown as replaced (approx)
  var SHINGLE = 3;
  function uniqueShingles(x, from, to){
    var seen = new Map();
    for(var i = from; i + SHINGLE <= to; i++){
      var key = x[i] + ',' + x[i + 1] + ',' + x[i + 2];
      seen.set(key, seen.has(key) ? -1 : i);
    }
    return seen;
  }
  /* Positions [i, j] of shingles unique on both sides, in order on both sides. */
  function anchorsOf(a, a0, a1, b, b0, b1){
    var inA = uniqueShingles(a, a0, a1), inB = uniqueShingles(b, b0, b1), pairs = [];
    inA.forEach(function(i, key){ var j = inB.get(key); if(i >= 0 && j !== undefined && j >= 0) pairs.push([i, j]); });
    pairs.sort(function(p, q){ return p[0] - q[0]; });
    // longest increasing run of j (patience sorting)
    var tails = [], prev = new Int32Array(pairs.length);
    pairs.forEach(function(pr, k){
      var lo = 0, hi = tails.length;
      while(lo < hi){ var mid = (lo + hi) >> 1; if(pairs[tails[mid]][1] < pr[1]) lo = mid + 1; else hi = mid; }
      prev[k] = lo > 0 ? tails[lo - 1] : -1;
      tails[lo] = k;
    });
    var chain = [];
    for(var k = tails.length ? tails[tails.length - 1] : -1; k >= 0; k = prev[k]) chain.push(pairs[k]);
    return chain.reverse();
  }
  function diffAnchored(a, a0, a1, b, b0, b1, st, chain){
    var i = a0, j = b0;
    chain.forEach(function(pr){
      if(pr[0] < i || pr[1] < j) return; // overlaps the previous shingle
      diffRange(a, i, pr[0], b, j, pr[1], st);
      i = pr[0] + SHINGLE; j = pr[1] + SHINGLE;
      st.done += 2 * SHINGLE;
    });
    diffRange(a, i, a1, b, j, b1, st);
  }
  function replaced(a0, a1, b0, b1, st){
    mark(st.delA, a0, a1); mark(st.insB, b0, b1);
    st.done += (a1 - a0) + (b1 - b0);
    st.tick();
  }
  function diffRange(a, a0, a1, b, b0, b1, st){
    while(a0 < a1 && b0 < b1 && a[a0] === b[b0]){ a0++; b0++; st.done += 2; }
    while(a0 < a1 && b0 < b1 && a[a1 - 1] === b[b1 - 1]){ a1--; b1--; st.done += 2; }
    if(a0 < a1 && b0 < b1 && (a1 - a0) + (b1 - b0) > ANCHOR_MIN){
      var chain = anchorsOf(a, a0, a1, b, b0, b1);
      if(chain.length){ diffAnchored(a, a0, a1, b, b0, b1, st, chain); return; }
      if((a1 - a0) + (b1 - b0) > MYERS_MAX){ st.approx = true; replaced(a0, a1, b0, b1, st); return; }
    }
    var mid = (a0 === a1 || b0 === b1) ? null : bisect(a, a0, a1 - a0, b, b0, b1 - b0, st.deadline);
    if(!mid || mid === TIMEOUT){
      if(mid === TIMEOUT) st.approx = true;
      replaced(a0, a1, b0, b1, st);
      return;
    }
    diffRange(a, a0, a0 + mid[0], b, b0, b0 + mid[1], st);
    diffRange(a, a0 + mid[0], a1, b, b0 + mid[1], b1, st);
  }
  /* a, b: word ids. delA[i] = 1: a[i] was removed; insB[j] = 1: b[j] was added. */
  function diffIds(a, b, onProgress){
    var total = a.length + b.length || 1, last = 0;
    var st = { delA: new Uint8Array(a.length), insB: new Uint8Array(b.length), approx: false, done: 0, deadline: Date.now() + DIFF_TIME_MS,
      tick: function(){ var now = Date.now(); if(now - last >= PROGRESS_EVERY_MS){ last = now; onProgress(st.done / total); } } };
    diffRange(a, 0, a.length, b, 0, b.length, st);
    return { delA: st.delA, insB: st.insB, approx: st.approx };
  }

  if(typeof document === 'undefined'){ // started as the worker (see diffInWorker)
    self.onmessage = function(e){
      var d = e.data || {};
      try{
        var r = diffIds(d.a, d.b, function(f){ self.postMessage({ type: 'progress', value: f }); });
        self.postMessage({ type: 'done', result: r }, [r.delA.buffer, r.insB.buffer]);
      }catch(err){ self.postMessage({ type: 'error', message: String(err && err.message || err) }); }
    };
    return;
  }

  /* ---------- the page ---------- */
  var LANG = (document.documentElement.getAttribute('lang') || 'en').slice(0, 2);
  var MSG = {
    en: {
      working: 'Comparing…',
      reading: 'Reading the text of {0}…',
      diffing: 'Looking for differences…',
      needTwo: 'Choose exactly 2 PDFs: the old version and the new one. There are {0} now; remove the ones you don\'t need.',
      order: 'Old: {0} → New: {1}',
      orderNeed: 'Add 2 PDFs: first the old version, then the new one.',
      openFail: 'Could not read {0}: {1}',
      noText: 'No text found in {0}. This tool compares text, not images (no OCR), so a scan without a text layer can\'t be compared.',
      tooLong: '{0} has too much text to compare here (more than {1} words).',
      emptyPages: '{0}: no text on {1} (a scan or a picture?); that part was not compared.',
      pageOne: 'page {0}', pagesSome: 'pages {0}',
      same: 'No differences in the text',
      info: '{0} changes · words added: {1}, removed: {2}',
      infoOne: '1 change · words added: {0}, removed: {1}',
      where: 'Changes on {0} of the new version.',
      approx: 'The documents differ a lot: in places whole passages are shown as removed and added instead of word by word.',
      limits: 'Only the text is compared: not the layout, images or formatting. A strongly changed layout can show extra differences.',
      head: 'Page {0}', headOld: 'Page {0} (old: page {1})', headOlds: 'Page {0} (old: pages {1}–{2})',
      del: 'removed', ins: 'added',
      pos: 'Change {0} of {1}',
      capped: 'The first {0} changes are shown here; the downloaded report has all {1}.',
      repTitle: 'Comparison: {0} and {1}',
      repOld: 'Old version', repNew: 'New version',
      repLegend: 'Removed text is struck through (−), added text is underlined (+).',
      repMade: 'Made with PDFCommunity on {0}, in the browser.'
    },
    nl: {
      working: 'Vergelijken…',
      reading: 'Tekst van {0} lezen…',
      diffing: 'Verschillen zoeken…',
      needTwo: 'Kies precies 2 pdf\'s: de oude versie en de nieuwe. Er staan er nu {0}; verwijder wat je niet nodig hebt.',
      order: 'Oud: {0} → Nieuw: {1}',
      orderNeed: 'Voeg 2 pdf\'s toe: eerst de oude versie, dan de nieuwe.',
      openFail: '{0} kon niet gelezen worden: {1}',
      noText: 'Geen tekst gevonden in {0}. Deze tool vergelijkt tekst, geen afbeeldingen (geen OCR), dus een scan zonder tekstlaag kan niet vergeleken worden.',
      tooLong: '{0} bevat te veel tekst om hier te vergelijken (meer dan {1} woorden).',
      emptyPages: '{0}: geen tekst op {1} (een scan of een afbeelding?); dat deel is niet vergeleken.',
      pageOne: 'pagina {0}', pagesSome: 'pagina\'s {0}',
      same: 'Geen verschillen in de tekst',
      info: '{0} wijzigingen · woorden toegevoegd: {1}, verwijderd: {2}',
      infoOne: '1 wijziging · woorden toegevoegd: {0}, verwijderd: {1}',
      where: 'Wijzigingen op {0} van de nieuwe versie.',
      approx: 'De documenten verschillen sterk: op sommige plaatsen staan hele passages als verwijderd en toegevoegd in plaats van woord per woord.',
      limits: 'Alleen de tekst wordt vergeleken: niet de opmaak, afbeeldingen of lay-out. Een sterk gewijzigde lay-out kan extra verschillen tonen.',
      head: 'Pagina {0}', headOld: 'Pagina {0} (oud: pagina {1})', headOlds: 'Pagina {0} (oud: pagina\'s {1}–{2})',
      del: 'verwijderd', ins: 'toegevoegd',
      pos: 'Wijziging {0} van {1}',
      capped: 'Hier staan de eerste {0} wijzigingen; het gedownloade rapport bevat ze alle {1}.',
      repTitle: 'Vergelijking: {0} en {1}',
      repOld: 'Oude versie', repNew: 'Nieuwe versie',
      repLegend: 'Verwijderde tekst is doorgehaald (−), toegevoegde tekst is onderstreept (+).',
      repMade: 'Gemaakt met PDFCommunity op {0}, in de browser.'
    },
    fr: {
      working: 'Comparaison…',
      reading: 'Lecture du texte de {0}…',
      diffing: 'Recherche des différences…',
      needTwo: 'Choisissez exactement 2 PDF : l\'ancienne version et la nouvelle. Il y en a {0} ; retirez ceux dont vous n\'avez pas besoin.',
      order: 'Ancien : {0} → Nouveau : {1}',
      orderNeed: 'Ajoutez 2 PDF : d\'abord l\'ancienne version, puis la nouvelle.',
      openFail: 'Impossible de lire {0} : {1}',
      noText: 'Aucun texte trouvé dans {0}. Cet outil compare du texte, pas des images (pas d\'OCR) : un scan sans couche de texte ne peut pas être comparé.',
      tooLong: '{0} contient trop de texte pour être comparé ici (plus de {1} mots).',
      emptyPages: '{0} : pas de texte sur {1} (un scan ou une image ?) ; cette partie n\'a pas été comparée.',
      pageOne: 'la page {0}', pagesSome: 'les pages {0}',
      same: 'Aucune différence dans le texte',
      info: '{0} modifications · mots ajoutés : {1}, supprimés : {2}',
      infoOne: '1 modification · mots ajoutés : {0}, supprimés : {1}',
      where: 'Modifications sur {0} de la nouvelle version.',
      approx: 'Les documents diffèrent beaucoup : par endroits, des passages entiers sont affichés comme supprimés puis ajoutés, au lieu d’être comparés mot à mot.',
      limits: 'Seul le texte est comparé : pas la mise en page, les images ni la mise en forme. Une mise en page très modifiée peut faire apparaître des différences en plus.',
      head: 'Page {0}', headOld: 'Page {0} (ancienne : page {1})', headOlds: 'Page {0} (ancienne : pages {1}–{2})',
      del: 'supprimé', ins: 'ajouté',
      pos: 'Modification {0} sur {1}',
      capped: 'Les {0} premières modifications sont affichées ici ; le rapport téléchargé les contient toutes ({1}).',
      repTitle: 'Comparaison : {0} et {1}',
      repOld: 'Ancienne version', repNew: 'Nouvelle version',
      repLegend: 'Le texte supprimé est barré (−), le texte ajouté est souligné (+).',
      repMade: 'Créé avec PDFCommunity le {0}, dans le navigateur.'
    },
    de: {
      working: 'Wird verglichen…',
      reading: 'Text von {0} wird gelesen…',
      diffing: 'Unterschiede werden gesucht…',
      needTwo: 'Wählen Sie genau 2 PDFs: die alte und die neue Version. Jetzt sind es {0}; entfernen Sie die, die Sie nicht brauchen.',
      order: 'Alt: {0} → Neu: {1}',
      orderNeed: 'Fügen Sie 2 PDFs hinzu: zuerst die alte Version, dann die neue.',
      openFail: '{0} konnte nicht gelesen werden: {1}',
      noText: 'Kein Text in {0} gefunden. Dieses Tool vergleicht Text, keine Bilder (keine OCR): Ein Scan ohne Textebene lässt sich nicht vergleichen.',
      tooLong: '{0} enthält zu viel Text, um ihn hier zu vergleichen (mehr als {1} Wörter).',
      emptyPages: '{0}: kein Text auf {1} (ein Scan oder ein Bild?); dieser Teil wurde nicht verglichen.',
      pageOne: 'Seite {0}', pagesSome: 'den Seiten {0}',
      same: 'Keine Unterschiede im Text',
      info: '{0} Änderungen · Wörter hinzugefügt: {1}, entfernt: {2}',
      infoOne: '1 Änderung · Wörter hinzugefügt: {0}, entfernt: {1}',
      where: 'Änderungen auf {0} der neuen Version.',
      approx: 'Die Dokumente unterscheiden sich stark: Stellenweise werden ganze Abschnitte als entfernt und hinzugefügt gezeigt statt Wort für Wort.',
      limits: 'Nur der Text wird verglichen, nicht Layout, Bilder oder Formatierung. Ein stark verändertes Layout kann zusätzliche Unterschiede zeigen.',
      head: 'Seite {0}', headOld: 'Seite {0} (alt: Seite {1})', headOlds: 'Seite {0} (alt: Seiten {1}–{2})',
      del: 'entfernt', ins: 'hinzugefügt',
      pos: 'Änderung {0} von {1}',
      capped: 'Hier stehen die ersten {0} Änderungen; der heruntergeladene Bericht enthält alle {1}.',
      repTitle: 'Vergleich: {0} und {1}',
      repOld: 'Alte Version', repNew: 'Neue Version',
      repLegend: 'Entfernter Text ist durchgestrichen (−), hinzugefügter Text ist unterstrichen (+).',
      repMade: 'Erstellt mit PDFCommunity am {0}, im Browser.'
    },
    es: {
      working: 'Comparando…',
      reading: 'Leyendo el texto de {0}…',
      diffing: 'Buscando diferencias…',
      needTwo: 'Elige exactamente 2 PDF: la versión antigua y la nueva. Ahora hay {0}; quita los que no necesites.',
      order: 'Antiguo: {0} → Nuevo: {1}',
      orderNeed: 'Añade 2 PDF: primero la versión antigua y después la nueva.',
      openFail: 'No se pudo leer {0}: {1}',
      noText: 'No se encontró texto en {0}. Esta herramienta compara texto, no imágenes (sin OCR), así que un escaneo sin capa de texto no se puede comparar.',
      tooLong: '{0} tiene demasiado texto para compararlo aquí (más de {1} palabras).',
      emptyPages: '{0}: sin texto en {1} (¿un escaneo o una imagen?); esa parte no se comparó.',
      pageOne: 'la página {0}', pagesSome: 'las páginas {0}',
      same: 'No hay diferencias en el texto',
      info: '{0} cambios · palabras añadidas: {1}, eliminadas: {2}',
      infoOne: '1 cambio · palabras añadidas: {0}, eliminadas: {1}',
      where: 'Cambios en {0} de la versión nueva.',
      approx: 'Los documentos son muy distintos: en algunos puntos se muestran pasajes enteros como eliminados y añadidos en lugar de palabra por palabra.',
      limits: 'Solo se compara el texto: no el diseño, las imágenes ni el formato. Un diseño muy cambiado puede mostrar diferencias de más.',
      head: 'Página {0}', headOld: 'Página {0} (antes: página {1})', headOlds: 'Página {0} (antes: páginas {1}–{2})',
      del: 'eliminado', ins: 'añadido',
      pos: 'Cambio {0} de {1}',
      capped: 'Aquí se muestran los primeros {0} cambios; el informe descargado los contiene todos ({1}).',
      repTitle: 'Comparación: {0} y {1}',
      repOld: 'Versión antigua', repNew: 'Versión nueva',
      repLegend: 'El texto eliminado está tachado (−) y el añadido, subrayado (+).',
      repMade: 'Creado con PDFCommunity el {0}, en el navegador.'
    },
    pt: {
      working: 'Comparando…',
      reading: 'Lendo o texto de {0}…',
      diffing: 'Procurando diferenças…',
      needTwo: 'Escolha exatamente 2 PDFs: a versão antiga e a nova. Agora há {0}; remova os que você não precisa.',
      order: 'Antigo: {0} → Novo: {1}',
      orderNeed: 'Adicione 2 PDFs: primeiro a versão antiga, depois a nova.',
      openFail: 'Não foi possível ler {0}: {1}',
      noText: 'Nenhum texto encontrado em {0}. Esta ferramenta compara texto, não imagens (sem OCR), então um documento escaneado sem camada de texto não pode ser comparado.',
      tooLong: '{0} tem texto demais para comparar aqui (mais de {1} palavras).',
      emptyPages: '{0}: sem texto em {1} (um documento escaneado ou uma imagem?); essa parte não foi comparada.',
      pageOne: 'a página {0}', pagesSome: 'as páginas {0}',
      same: 'Nenhuma diferença no texto',
      info: '{0} alterações · palavras adicionadas: {1}, removidas: {2}',
      infoOne: '1 alteração · palavras adicionadas: {0}, removidas: {1}',
      where: 'Alterações em {0} da nova versão.',
      approx: 'Os documentos são muito diferentes: em alguns trechos, passagens inteiras aparecem como removidas e adicionadas em vez de palavra por palavra.',
      limits: 'Só o texto é comparado: não o layout, as imagens nem a formatação. Um layout muito alterado pode mostrar diferenças a mais.',
      head: 'Página {0}', headOld: 'Página {0} (antes: página {1})', headOlds: 'Página {0} (antes: páginas {1}–{2})',
      del: 'removido', ins: 'adicionado',
      pos: 'Alteração {0} de {1}',
      capped: 'Aqui aparecem as primeiras {0} alterações; o relatório baixado tem todas as {1}.',
      repTitle: 'Comparação: {0} e {1}',
      repOld: 'Versão antiga', repNew: 'Versão nova',
      repLegend: 'O texto removido está riscado (−) e o adicionado, sublinhado (+).',
      repMade: 'Feito com PDFCommunity em {0}, no navegador.'
    },
    it: {
      working: 'Confronto in corso…',
      reading: 'Lettura del testo di {0}…',
      diffing: 'Ricerca delle differenze…',
      needTwo: 'Scegli esattamente 2 PDF: la versione vecchia e quella nuova. Ora ce ne sono {0}; rimuovi quelli che non ti servono.',
      order: 'Vecchio: {0} → Nuovo: {1}',
      orderNeed: 'Aggiungi 2 PDF: prima la versione vecchia, poi quella nuova.',
      openFail: 'Impossibile leggere {0}: {1}',
      noText: 'Nessun testo trovato in {0}. Questo strumento confronta il testo, non le immagini (niente OCR): una scansione senza livello di testo non si può confrontare.',
      tooLong: '{0} contiene troppo testo per confrontarlo qui (più di {1} parole).',
      emptyPages: '{0}: nessun testo a {1} (una scansione o un\'immagine?); quella parte non è stata confrontata.',
      pageOne: 'pagina {0}', pagesSome: 'pagine {0}',
      same: 'Nessuna differenza nel testo',
      info: '{0} modifiche · parole aggiunte: {1}, rimosse: {2}',
      infoOne: '1 modifica · parole aggiunte: {0}, rimosse: {1}',
      where: 'Modifiche a {0} della nuova versione.',
      approx: 'I documenti sono molto diversi: in alcuni punti interi passaggi sono mostrati come rimossi e aggiunti invece che parola per parola.',
      limits: 'Si confronta solo il testo: non l\'impaginazione, le immagini o la formattazione. Un\'impaginazione molto cambiata può mostrare differenze in più.',
      head: 'Pagina {0}', headOld: 'Pagina {0} (prima: pagina {1})', headOlds: 'Pagina {0} (prima: pagine {1}–{2})',
      del: 'rimosso', ins: 'aggiunto',
      pos: 'Modifica {0} di {1}',
      capped: 'Qui sono mostrate le prime {0} modifiche; il report scaricato le contiene tutte ({1}).',
      repTitle: 'Confronto: {0} e {1}',
      repOld: 'Versione vecchia', repNew: 'Versione nuova',
      repLegend: 'Il testo rimosso è barrato (−), quello aggiunto è sottolineato (+).',
      repMade: 'Creato con PDFCommunity il {0}, nel browser.'
    }, pl: {
      working: "Porównywanie…",
      reading: "Odczytywanie tekstu z {0}…",
      diffing: "Szukanie różnic…",
      needTwo: "Wybierz dokładnie 2 pliki PDF: starszą i nowszą wersję. Teraz wybrano: {0}; usuń te, których nie potrzebujesz.",
      order: "Starsza: {0} → Nowsza: {1}",
      orderNeed: "Dodaj 2 pliki PDF: najpierw starszą wersję, potem nowszą.",
      openFail: "Nie udało się odczytać {0}: {1}",
      noText: "Nie znaleziono tekstu w {0}. To narzędzie porównuje tekst, a nie obrazy (bez OCR): skanu bez warstwy tekstowej nie da się porównać.",
      tooLong: "{0} zawiera za dużo tekstu, żeby go tu porównać (ponad {1} słów).",
      emptyPages: "{0}: brak tekstu na {1} (skan lub obraz?); ta część nie została porównana.",
      pageOne: "stronie {0}", pagesSome: "stronach {0}",
      same: "Brak różnic w tekście",
      info: "Zmiany: {0} · dodane słowa: {1}, usunięte: {2}",
      infoOne: "1 zmiana · dodane słowa: {0}, usunięte: {1}",
      where: "Zmiany na {0} nowszej wersji.",
      approx: "Dokumenty bardzo się różnią: miejscami całe fragmenty są pokazane jako usunięte i dodane zamiast słowo po słowie.",
      limits: "Porównywany jest tylko tekst: nie układ, obrazy ani formatowanie. Mocno zmieniony układ może pokazać dodatkowe różnice.",
      head: "Strona {0}", headOld: "Strona {0} (wcześniej: strona {1})", headOlds: "Strona {0} (wcześniej: strony {1}–{2})",
      del: "usunięte", ins: "dodane",
      pos: "Zmiana {0} z {1}",
      capped: "Tutaj pokazano pierwsze zmiany ({0}); pobrany raport zawiera wszystkie ({1}).",
      repTitle: "Porównanie: {0} i {1}",
      repOld: "Starsza wersja", repNew: "Nowsza wersja",
      repLegend: "Usunięty tekst jest przekreślony (−), dodany – podkreślony (+).",
      repMade: "Utworzono w PDFCommunity {0}, w przeglądarce."
    }
  };
  var M = MSG[LANG] || MSG.en;
  function t(key){
    var s = M[key] != null ? M[key] : MSG.en[key];
    return pcFill.apply(null, [s].concat(Array.prototype.slice.call(arguments, 1)));
  }
  function userFacing(msg){ var e = new Error(msg); e.userFacing = true; return e; }
  var $ = function(id){ return document.getElementById(id); };

  var MAX_WORDS = 1500000;   // per document: ids, pages and flags stay well under 50 MB
  var CONTEXT_WORDS = 12;    // unchanged words shown around a change
  var VIEW_MAX_CHANGES = 1500; // in the page; the report has all
  var READ_SHARE = 0.45;     // progress: each document's text, then the diff
  var WORKER_URL = '/tool-compare.js' + PC_REL;
  var HYPHEN_EOL = /(\p{L})[-‐]\r?\n(?=\p{Ll})/gu; // "agree-\nment" → "agreement"
  var NO_SPACE_SCRIPT = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]|[^\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]+/gu;

  /* One page's text as words: ligatures and full-width forms folded (NFKC),
     soft hyphens dropped, a word broken at the end of a line joined again.
     Chinese/Japanese have no spaces: each character is a word of its own. */
  function addPageWords(doc, text, page){
    var s = text.normalize('NFKC').replace(/­/g, '').replace(HYPHEN_EOL, '$1');
    var before = doc.w.length;
    s.split(/\s+/).forEach(function(chunk){
      if(!chunk) return;
      chunk.match(NO_SPACE_SCRIPT).forEach(function(piece, k){
        doc.w.push(piece); doc.p.push(page); doc.sp.push(k === 0);
      });
    });
    return doc.w.length > before;
  }
  function readError(file, e){
    if(pcIsCancel(e)) return e;
    pcThrowIfOutOfMemory(e);
    pcLogUnexpected(e);
    if(e && (e.name === 'PasswordException' || e.pw)) return PCTools.userError('password'); // the status box asks for it
    return userFacing(t('openFail', file.name, PCTools.reasonOf(e, 'invalidPdf')));
  }
  async function readDoc(file, onFrac, halt){
    var bytes = new Uint8Array(await file.arrayBuffer()), task = null, doc = { w: [], p: [], sp: [], empty: [], pages: 0 };
    try{
      var pdf;
      try{ task = pcGetDocument(pcPdfSource(bytes)); pdf = await task.promise; }
      catch(e){ throw readError(file, e); }
      if(await pcIsPureXfaJs(pdf)) throw pcUserError(pcT('xfa'));
      doc.pages = pdf.numPages;
      for(var p = 1; p <= doc.pages; p++){
        pcCheckCancel();
        halt(); // the other file failed: stop reading this one
        var page = await pdf.getPage(p);
        try{
          if(!addPageWords(doc, PCTools.txtPageText(await page.getTextContent(), false), p)) doc.empty.push(p);
        }finally{ page.cleanup(); }
        if(doc.w.length > MAX_WORDS) throw userFacing(t('tooLong', file.name, MAX_WORDS.toLocaleString(document.documentElement.lang)));
        onFrac(p / doc.pages);
      }
      if(!doc.w.length) throw userFacing(t('noText', file.name));
      return doc;
    }catch(e){
      if(pcIsCancel(e) || (e && e.userFacing)) throw e;
      throw readError(file, e);
    }finally{
      if(task){ try{ await task.destroy(); }catch(e){ pcLogUnexpected(e); } } // cleanup only
    }
  }
  /* The same word gets the same number in both documents. */
  function wordIds(A, B){
    var ids = new Map();
    var conv = function(words){
      var out = new Int32Array(words.length);
      for(var k = 0; k < words.length; k++){
        var id = ids.get(words[k]);
        if(id === undefined){ id = ids.size; ids.set(words[k], id); }
        out[k] = id;
      }
      return out;
    };
    return [conv(A.w), conv(B.w)];
  }
  /* The diff in its own worker; without one (or if it fails to start), on the page. */
  function diffInWorker(a, b, onFrac){
    return new Promise(function(resolve, reject){
      var w;
      var onPage = function(){
        try{ resolve(diffIds(a, b, onFrac)); }catch(e){ reject(e); }
      };
      try{ w = new Worker(WORKER_URL); }
      catch(e){ pcLogUnexpected(e); onPage(); return; }
      var end = function(){ w.terminate(); pcCancelHooks.delete(cancel); };
      var cancel = function(){ end(); reject(pcCancelError()); };
      pcCancelHooks.add(cancel);
      w.onmessage = function(e){
        var m = e.data || {};
        if(m.type === 'progress'){ onFrac(m.value); return; }
        end();
        if(m.type === 'done') resolve(m.result);
        else reject(new Error('compare worker: ' + m.message));
      };
      w.onerror = function(e){
        e.preventDefault();
        end();
        pcLogUnexpected(new Error('compare worker: ' + (e.message || 'load error'))); // a trace; the page does the work
        onPage();
      };
      w.postMessage({ a: a, b: b }); // copied, not transferred: onPage may still need them
    });
  }

  /* ---------- the result as pages with runs ---------- */
  /* Walks both documents along the diff. A section = one page of the new
     version (a removal sits on the page of the word before it); a run =
     words with the same state. blocks = changes (removed + added side by side = one). */
  function buildModel(A, B, res){
    var model = { sections: [], added: 0, removed: 0, blocks: 0 };
    var i = 0, j = 0, n = A.w.length, m = B.w.length, sec = null, run = null, prevOp = 'eq';
    while(i < n || j < m){
      var op, w, sp, pNew, pOld = 0;
      if(i < n && res.delA[i]){ op = 'del'; w = A.w[i]; sp = A.sp[i]; pOld = A.p[i]; pNew = B.p[j > 0 ? j - 1 : 0]; i++; model.removed++; }
      else if(j < m && res.insB[j]){ op = 'ins'; w = B.w[j]; sp = B.sp[j]; pNew = B.p[j]; j++; model.added++; }
      else{
        if(i >= n || j >= m || A.w[i] !== B.w[j]) throw new Error('compare: diff out of step at ' + i + '/' + j);
        op = 'eq'; w = B.w[j]; sp = B.sp[j]; pNew = B.p[j]; pOld = A.p[i]; i++; j++;
      }
      if(op !== 'eq' && prevOp === 'eq') model.blocks++;
      prevOp = op;
      if(!sec || sec.page !== pNew){ sec = { page: pNew, oldMin: 0, oldMax: 0, changed: false, runs: [] }; model.sections.push(sec); run = null; }
      if(pOld){ sec.oldMin = sec.oldMin ? Math.min(sec.oldMin, pOld) : pOld; sec.oldMax = Math.max(sec.oldMax, pOld); }
      if(op !== 'eq') sec.changed = true;
      if(!run || run.op !== op){ run = { op: op, words: [], sp: [], block: op === 'eq' ? 0 : model.blocks }; sec.runs.push(run); }
      run.words.push(w); run.sp.push(sp);
    }
    model.sections = model.sections.filter(function(s){ return s.changed; });
    return model;
  }
  function joinWords(words, sp, from, to){
    var s = '';
    for(var k = from; k < to; k++) s += (s && sp[k] ? ' ' : '') + words[k];
    return s;
  }
  /* An unchanged run: only the words next to a change, "…" for the rest. */
  function eqText(run, first, last){
    var n = run.words.length, C = CONTEXT_WORDS;
    if(first && last) return joinWords(run.words, run.sp, 0, n);
    if(first) return n > C ? '… ' + joinWords(run.words, run.sp, n - C, n) : joinWords(run.words, run.sp, 0, n);
    if(last) return n > C ? joinWords(run.words, run.sp, 0, C) + ' …' : joinWords(run.words, run.sp, 0, n);
    return n > 2 * C + 1 ? joinWords(run.words, run.sp, 0, C) + ' … ' + joinWords(run.words, run.sp, n - C, n) : joinWords(run.words, run.sp, 0, n);
  }
  function sectionHead(s){
    if(!s.oldMin || (s.oldMin === s.page && s.oldMax === s.page)) return t('head', s.page);
    return s.oldMin === s.oldMax ? t('headOld', s.page, s.oldMin) : t('headOlds', s.page, s.oldMin, s.oldMax);
  }
  /* Each run as {op, text, space (before it), block}. */
  function sectionParts(s){
    return s.runs.map(function(r, k){
      var text = r.op === 'eq' ? eqText(r, k === 0, k === s.runs.length - 1) : joinWords(r.words, r.sp, 0, r.words.length);
      return { op: r.op, text: text, space: k > 0 && r.sp[0], block: r.block };
    });
  }
  /* "2, 5–7, 9" */
  function pageRanges(pages){
    var out = [], k = 0;
    while(k < pages.length){
      var a = pages[k];
      while(k + 1 < pages.length && pages[k + 1] === pages[k] + 1) k++;
      out.push(a === pages[k] ? String(a) : a + '–' + pages[k]);
      k++;
    }
    return out.join(', ');
  }
  function pagesText(pages){ return pages.length === 1 ? t('pageOne', pages[0]) : t('pagesSome', pageRanges(pages)); }
  function infoText(model){
    if(!model.blocks) return t('same');
    return model.blocks === 1 ? t('infoOne', model.added, model.removed) : t('info', model.blocks, model.added, model.removed);
  }
  function notesFor(names, docs, approx){
    var n = docs.map(function(d, k){ return d.empty.length ? t('emptyPages', names[k], pagesText(d.empty)) : ''; });
    if(approx) n.push(t('approx'));
    return n.filter(Boolean).join(' ');
  }

  /* ---------- in the page ---------- */
  function partNode(part){
    if(part.op === 'eq') return document.createTextNode(part.text);
    var el = document.createElement(part.op);
    el.className = 'pdiff-' + part.op;
    el.dataset.b = part.block;
    var sr = document.createElement('span');
    sr.className = 'pdiff-sr';
    sr.textContent = t(part.op) + ': ';
    el.append(sr, part.text);
    return el;
  }
  function firstBlock(parts){
    for(var k = 0; k < parts.length; k++) if(parts[k].op !== 'eq') return parts[k].block;
    return 0;
  }
  function renderView(model){
    var out = $('compare-out');
    out.replaceChildren();
    for(var k = 0; k < model.sections.length; k++){
      var s = model.sections[k], parts = sectionParts(s);
      if(firstBlock(parts) > VIEW_MAX_CHANGES) break;
      var box = document.createElement('section'), h = document.createElement('h2'), p = document.createElement('p');
      box.className = 'pdiff-page'; h.className = 'pdiff-head'; p.className = 'pdiff-text';
      h.textContent = sectionHead(s);
      parts.forEach(function(part){
        if(part.op !== 'eq' && part.block > VIEW_MAX_CHANGES) return;
        if(part.space) p.append(' ');
        p.append(partNode(part));
      });
      box.append(h, p);
      out.append(box);
    }
    var cap = $('compare-capped');
    cap.hidden = model.blocks <= VIEW_MAX_CHANGES;
    cap.textContent = cap.hidden ? '' : t('capped', VIEW_MAX_CHANGES, model.blocks);
  }
  var nav = { pos: -1, count: 0 };
  function blockEl(k){ return $('compare-out').querySelector('[data-b="' + (k + 1) + '"]'); }
  function goTo(k){
    var old = nav.pos >= 0 && blockEl(nav.pos);
    if(old) old.classList.remove('pdiff-cur');
    nav.pos = k;
    var el = blockEl(k);
    if(el){
      el.classList.add('pdiff-cur');
      el.tabIndex = -1;
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    updateNav();
  }
  function updateNav(){
    $('compare-pos').textContent = nav.pos >= 0 ? t('pos', nav.pos + 1, nav.count) : '';
    $('compare-prev').disabled = nav.pos <= 0;
    $('compare-next').disabled = nav.pos >= nav.count - 1;
  }
  function showView(model, notes){
    var view = $('compare-view');
    $('compare-summary').textContent = model.blocks ? t('where', pagesText(model.sections.map(function(s){ return s.page; }))) : t('same') + '.';
    $('compare-notes').textContent = notes;
    $('compare-notes').hidden = !notes;
    renderView(model);
    nav.pos = -1; nav.count = Math.min(model.blocks, VIEW_MAX_CHANGES);
    $('compare-nav').hidden = !nav.count;
    updateNav();
    view.hidden = false;
  }
  function hideView(){
    var view = $('compare-view');
    if(!view || view.hidden) return;
    view.hidden = true;
    $('compare-out').replaceChildren(); // the text of both files leaves the page's memory
  }

  /* ---------- the report (one HTML file, no scripts, no external files) ---------- */
  var REPORT_CSS = 'body{font:16px/1.6 system-ui,sans-serif;max-width:52em;margin:0 auto;padding:24px 16px;color:#1e271f;background:#fff}' +
    'h1{font-size:1.4rem;margin:0 0 .4em}h2{font-size:1.05rem;margin:1.6em 0 .3em;padding-top:.6em;border-top:1px solid #ccc}' +
    'dl{display:grid;grid-template-columns:max-content 1fr;gap:2px 12px;margin:0 0 1em}dt{font-weight:600}dd{margin:0;overflow-wrap:anywhere}' +
    '.note{color:#4c5750;font-size:.92rem}p{overflow-wrap:anywhere}' +
    'del,ins{padding:0 2px;border-radius:3px;text-decoration-thickness:2px}' +
    'del{background:rgba(220,38,38,.16);text-decoration-line:line-through;text-decoration-color:#b91c1c}' +
    'ins{background:rgba(22,163,74,.18);text-decoration-line:underline;text-decoration-color:#15803d}' +
    'del::before{content:"\\2212\\00a0";font-weight:700}ins::before{content:"+\\00a0";font-weight:700}' +
    '.sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}' +
    '@media (prefers-color-scheme:dark){body{background:#101b18;color:#d9e2da}.note{color:#bdc1bd}h2{border-color:#2b3b36}' +
    'del{background:rgba(248,113,113,.22);text-decoration-color:#f87171}ins{background:rgba(74,222,128,.2);text-decoration-color:#4ade80}}';
  function reportPart(part){
    var text = escapeHtml(part.text);
    if(part.op === 'eq') return text;
    return '<' + part.op + '><span class="sr">' + escapeHtml(t(part.op)) + ': </span>' + text + '</' + part.op + '>';
  }
  function reportHtml(names, model, info, notes){
    var title = escapeHtml(t('repTitle', names[0], names[1]));
    var body = model.sections.map(function(s){
      return '<h2>' + escapeHtml(sectionHead(s)) + '</h2>\n<p>' + sectionParts(s).map(function(part){ return (part.space ? ' ' : '') + reportPart(part); }).join('') + '</p>';
    }).join('\n');
    var made = new Date().toLocaleString(document.documentElement.lang, { dateStyle: 'medium', timeStyle: 'short' });
    return '<!DOCTYPE html>\n<html lang="' + escapeHtml(document.documentElement.lang) + '"><head><meta charset="utf-8">' +
      '<meta name="viewport" content="width=device-width, initial-scale=1"><meta name="color-scheme" content="light dark">' +
      '<title>' + title + '</title><style>' + REPORT_CSS + '</style></head><body>\n<h1>' + title + '</h1>\n' +
      '<dl><dt>' + escapeHtml(t('repOld')) + '</dt><dd>' + escapeHtml(names[0]) + '</dd><dt>' + escapeHtml(t('repNew')) + '</dt><dd>' + escapeHtml(names[1]) + '</dd></dl>\n' +
      '<p><strong>' + escapeHtml(info) + '</strong></p>\n' +
      (notes ? '<p class="note">' + escapeHtml(notes) + '</p>\n' : '') +
      '<p class="note">' + escapeHtml(t('repLegend')) + ' ' + escapeHtml(t('limits')) + '</p>\n' +
      body + '\n<p class="note">' + escapeHtml(t('repMade', made)) + '</p>\n</body></html>\n';
  }

  /* ---------- the tool ---------- */
  function showOrder(files){
    var el = $('compare-order'), swap = $('compare-swap');
    if(!el) return;
    el.textContent = files.length === 2 ? t('order', files[0].name, files[1].name) : files.length > 2 ? t('needTwo', files.length) : t('orderNeed');
    swap.hidden = files.length !== 2;
  }
  async function compareRun(files, opt, ctx){
    hideView();
    if(files.length !== 2) throw userFacing(t('needTwo', files.length));
    pcRequirePdfJs();
    // both at once: pdf.js reads each file in a worker of its own (about twice as fast on 2+ cores)
    ctx.setStatus(t('reading', files[0].name + ' + ' + files[1].name));
    var fracs = [0, 0], failed = null;
    var halt = function(){ if(failed) throw failed; };
    var docs = await Promise.all(files.map(function(f, k){
      return readDoc(f, function(fr){ fracs[k] = fr; ctx.setProgress((fracs[0] + fracs[1]) * READ_SHARE); }, halt)
        .catch(function(e){ failed = failed || e; throw e; });
    }));
    ctx.setStatus(t('diffing'));
    var ids = wordIds(docs[0], docs[1]);
    var res = await diffInWorker(ids[0], ids[1], function(f){ ctx.setProgress(2 * READ_SHARE + f * (1 - 2 * READ_SHARE)); });
    pcCheckCancel();
    var names = [files[0].name, files[1].name];
    var model = buildModel(docs[0], docs[1], res);
    var info = infoText(model), notes = notesFor(names, docs, res.approx);
    var html = reportHtml(names, model, info, notes);
    showView(model, notes);
    return {
      results: [{ blob: new Blob([html], { type: 'text/html;charset=utf-8' }), filename: baseFileName(names[0]) + '-vs-' + baseFileName(names[1]) + '-compare.html' }],
      skipped: [], info: info, note: ''
    };
  }
  pcOnPanel('compare', function(){
    var state = PCTools.setupTool('compare', {
      accept: PCTools.isPdf, minFiles: 2, reorder: true, working: function(){ return t('working'); }, zipName: 'pdf-compare',
      onFiles: function(files){ showOrder(files); hideView(); },
      run: compareRun
    });
    if(!state) return;
    $('compare-swap').addEventListener('click', function(){
      if(state.files.length === 2) fileHub.move(state.files[1], -1, PCTools.isPdf);
    });
    $('compare-prev').addEventListener('click', function(){ if(nav.pos > 0) goTo(nav.pos - 1); });
    $('compare-next').addEventListener('click', function(){ if(nav.pos < nav.count - 1) goTo(nav.pos + 1); });
  });

  // Exposed for automated tests only.
  window.PDFC_COMPARE = { diffIds: diffIds, addPageWords: addPageWords, buildModel: buildModel, pageRanges: pageRanges };
})();
